import { useState } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import {
  WORKFLOW_DOCUMENT_LOCALES,
  type WorkflowDocumentLocale,
  type WorkflowDocumentTemplate,
} from '@munifocus/contracts-gov';
import { pickLang, useGenerateWorkflowDocument } from '../../lib/bylaw-workflows-api';

/**
 * The letters a step produces. Generated as a PDF from what is recorded and
 * attached to the file's documents; the officer reviews it there before it is
 * served. Bilingual by default — equal weight, both languages on one letter.
 */
export function WorkflowDocumentActions({
  fileId,
  templates,
  disabled,
}: {
  fileId: string;
  templates: WorkflowDocumentTemplate[];
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const generate = useGenerateWorkflowDocument(fileId);
  const [locale, setLocale] = useState<WorkflowDocumentLocale>('bilingual');

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{t('gov.bylawWorkflow.documents.title')}</Typography>
      <FormControl size="small" sx={{ maxWidth: 240 }} disabled={disabled}>
        <InputLabel>{t('gov.bylawWorkflow.documents.language')}</InputLabel>
        <Select
          label={t('gov.bylawWorkflow.documents.language')}
          aria-label={t('gov.bylawWorkflow.documents.language')}
          value={locale}
          onChange={(e) => setLocale(e.target.value as WorkflowDocumentLocale)}
        >
          {WORKFLOW_DOCUMENT_LOCALES.map((l) => (
            <MenuItem key={l} value={l}>
              {t(`gov.bylawWorkflow.documents.locales.${l}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {templates.map((template) => (
          <Button
            key={template.key}
            variant="outlined"
            size="small"
            disabled={disabled || generate.isPending}
            onClick={() => generate.mutate({ templateKey: template.key, locale })}
          >
            {t('gov.bylawWorkflow.documents.generate', {
              title: pickLang(template.title, i18n.language),
            })}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}
